import { jsxs, jsx } from 'react/jsx-runtime';
import { cssVar } from '@fabric-app/ui/theme/variables';
import { MarkdownRender } from '@fabric-app/ui/markdown/components/MarkdownRender';
import { styled } from 'styled-components';
import { Flex } from '@fabric-app/ui/components/Flex';
import { H1, P } from '@fabric-app/ui/components/Typography';
import { getDomain } from '@fabric-app/utils/url';
import { useResourceBookmarkDataSafe } from '../../resource-detail/components/context/resourceDataContext.js';
import { calculateReadingTime } from '@fabric-app/utils/text';
import dayjs from 'dayjs';
import '../../../node_modules/dayjs/plugin/duration.js';
import Divider from '@fabric-app/ui/components/Divider';
import { mediaMobile } from '@fabric-app/ui/constants/styled-utils';
import { useScrollPosition } from '@fabric-app/hooks/useScrollPosition';
import { useRef, useMemo, useEffect } from 'react';
import { useExpandedFdocContext } from '../../resource-detail/components/ExpandedFdoc/ExpandedFdocProvider.js';
import { useUnmount } from '@fabric-app/hooks/useUnmount';
import { useResponsive } from '@fabric-app/ui/hooks/useResponsive';

const Container = styled(Flex).attrs({
  alignItems: "center",
  direction: "column",
  flexGrow: 1,
  justifyContent: "center",
  gap: 24
}).withConfig({
  displayName: "PageResourceMarkdown__Container",
  componentId: "sc-6633547-0"
})([
  `background:`,
  `;padding:24px 66px 66px 66px;`,
  `{padding:24px 24px 120px 24px;[data-hide-bottom-bar='true'] &{padding-bottom:calc(env(safe-area-inset-bottom) + 1rem + 24px + 4px);}[data-hide-top-bar='true'] &{padding-top:calc(24px + var(--safe-margin-offset-top) + 4rem);}}`
], cssVar["color-bg-primary"], mediaMobile);
const MarkdownContent = styled(MarkdownRender).withConfig({
  displayName: "PageResourceMarkdown__MarkdownContent",
  componentId: "sc-6633547-1"
})([
  `--heading-scale:1.2;--h1-size:calc(1em * 1.6);--h2-size:calc(1em * 1.4);--h3-size:calc(1em * 1.2);font-size:1.188rem;max-width:700px;color:`,
  `;line-height:160%;font-family:'Roboto Serif',serif;font-weight:500;width:100%;*{font-family:'Roboto Serif',serif;}overflow-wrap:break-word;word-break:break-word;display:grid;> *{--margin-block-start:0.5em;--margin-block-end:0.5em;}`
], cssVar["color-text-primary"]);
const Title = styled(H1).withConfig({
  displayName: "PageResourceMarkdown__Title",
  componentId: "sc-6633547-2"
})([
  `font-size:1.875rem;font-weight:400;font-family:'Young Serif',serif;letter-spacing:-0.6px;line-height:normal;text-align:center;`
]);
const InfoBox = styled(Flex).attrs({
  direction: "column",
  justifyContent: "center"
}).withConfig({
  displayName: "PageResourceMarkdown__InfoBox",
  componentId: "sc-6633547-3"
})([
  `position:absolute;bottom:0;left:0;width:100%;background:`,
  `;[data-hide-bottom-bar='true'] &{padding-bottom:calc(env(safe-area-inset-bottom) + 1rem);}display:none;`,
  `{[data-hide-bottom-bar='true'] &{display:block;}}`
], cssVar["color-bg-primary"], mediaMobile);
const InfoBoxTitle = styled(P).attrs({
  size: "xxs",
  weight: 400,
  ellipsis: true
}).withConfig({
  displayName: "PageResourceMarkdown__InfoBoxTitle",
  componentId: "sc-6633547-4"
})([
  `padding:4px 12px;text-align:center;`
]);
const Progress = styled.div.withConfig({
  displayName: "PageResourceMarkdown__Progress",
  componentId: "sc-6633547-5"
})([
  `width:100%;height:4px;background:`,
  `;`
], cssVar["color-bg-quarternary"]);
const ProgressBar = styled.div.withConfig({
  displayName: "PageResourceMarkdown__ProgressBar",
  componentId: "sc-6633547-6"
})([
  `width:calc(var(--progress) * 1%);height:3px;background:`,
  `;border-radius:0 2px 2px 0;transition:width 0.2s cubic-bezier(0.44,0.59,0.57,1);`
], cssVar["color-app-primary"]);
const PageResourceMarkdown = ({ markdown, viewportRef }) => {
  const { resource } = useResourceBookmarkDataSafe();
  const { isMobileView } = useResponsive();
  const { setHideBottomBar, setHideTopBar, hideTopBar } = useExpandedFdocContext();
  const hideTopBarRef = useRef(hideTopBar);
  hideTopBarRef.current = hideTopBar;
  const [scrollPosition] = useScrollPosition(viewportRef, {
    onScroll: ({ scrollPosition: scrollPosition2, direction, duration, reset }) => {
      if (!isMobileView || scrollPosition2.totalY <= 200 && !hideTopBarRef.current) {
        return;
      }
      if (scrollPosition2.y === 0) {
        setHideBottomBar(false);
        setHideTopBar(false);
        reset();
        return;
      }
      if (direction.y.direction === "down" && direction.y.distance > 30 && duration > 0) {
        setHideBottomBar(true);
        setHideTopBar(true);
        reset();
        return;
      }
      if (direction.y.direction === "up" && direction.y.distance < -60 && duration > 0) {
        setHideBottomBar(false);
        setHideTopBar(false);
        reset();
        return;
      }
    }
  });
  const url = resource.originUrl ?? resource.data.attributes?.pageUrl ?? resource.data.webpage?.url ?? "";
  const title = resource.data.attributes?.pageTitle ?? resource.data.webpage?.title ?? resource.name ?? "";
  const domain = getDomain(url).toUpperCase();
  const readingTimeSeconds = calculateReadingTime(markdown);
  const humanReadingTime = dayjs.duration(readingTimeSeconds, "seconds").humanize();
  const total = useMemo(() => {
    if (!scrollPosition || scrollPosition.totalY === 0) return 0;
    return Math.round(scrollPosition.y * 100 / scrollPosition.totalY);
  }, [
    scrollPosition
  ]);
  useUnmount(() => {
    setHideBottomBar(false);
    setHideTopBar(false);
  });
  useEffect(() => {
    if (isMobileView) return;
    setHideBottomBar(false);
    setHideTopBar(false);
  }, [
    isMobileView,
    setHideBottomBar,
    setHideTopBar
  ]);
  return /* @__PURE__ */ jsxs(Container, {
    children: [
      /* @__PURE__ */ jsx(P, {
        color: "tertiary",
        size: "lg",
        weight: 400,
        children: domain
      }),
      /* @__PURE__ */ jsx(Title, {
        color: "primary",
        children: title
      }),
      /* @__PURE__ */ jsxs(P, {
        color: "tertiary",
        size: "sm",
        weight: 400,
        children: [
          "Reading time: ",
          humanReadingTime
        ]
      }),
      /* @__PURE__ */ jsx(Divider, {}),
      /* @__PURE__ */ jsx(MarkdownContent, {
        "data-id": "page-resource-markdown",
        markdown,
        parserOptions: {
          fixMalformedLists: true,
          fixSpacedStrong: true,
          includeEmptyLines: false,
          removeTitleContent: {
            title
          }
        }
      }),
      /* @__PURE__ */ jsxs(InfoBox, {
        children: [
          /* @__PURE__ */ jsx(Progress, {
            children: /* @__PURE__ */ jsx(ProgressBar, {
              style: {
                "--progress": total
              }
            })
          }),
          /* @__PURE__ */ jsx(InfoBoxTitle, {
            children: title
          })
        ]
      })
    ]
  });
};

export { PageResourceMarkdown };
//# sourceMappingURL=PageResourceMarkdown.js.map
